import React, { Component } from 'react'
import Chart from 'chart.js/auto';
import classes from "./Graph.module.css";
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import GraphZoomControls from './GraphZoomControls.js';

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

const DAY_MS = 24 * 60 * 60 * 1000

const getXAxisDisplayConfig = (min, max) => {
    const dayCount = Math.max(1, Math.ceil((max - min) / DAY_MS))
    if (dayCount <= 2) return {unit: 'hour', stepSize: 3, maxTicks: 10}
    if (dayCount <= 14) return {unit: 'day', stepSize: 1, maxTicks: 10}
    if (dayCount <= 70) return {unit: 'day', stepSize: 7, maxTicks: 11}
    if (dayCount <= 730) {
        const monthCount = Math.max(1, Math.ceil(dayCount / 30))
        return {unit: 'month', stepSize: Math.max(1, Math.ceil(monthCount / 8)), maxTicks: 10}
    }
    const yearCount = Math.max(1, Math.ceil(dayCount / 365))
    return {unit: 'year', stepSize: Math.max(1, Math.ceil(yearCount / 8)), maxTicks: 10}
}

class GraphThreeCurves extends Component {
    constructor(props){
        super(props);
        this.myChart = "";
        this.state = { showHint: true }
        this.handleDoubleClick = this.handleDoubleClick.bind(this)
    }

    getCanvas() {
        let ctx = document.getElementById(this.props.id_graph);
        if (ctx === null && this.props.new_window) {
            ctx = this.props.new_window.document.getElementById(this.props.id_graph);
        }
        return ctx
    }

    buildConnectorData() {
        if (!this.props.connectCurve2Curve3) return []

        const curve2 = this.props.curve2 || []
        const curve3 = this.props.curve3 || []
        const curve3ByX = new Map()
        curve3.forEach(point => {
            if (point && point.y !== undefined && point.y !== null) {
                curve3ByX.set(point.x, point.y)
            }
        })

        const connector = []
        curve2.forEach(point => {
            if (!point || point.y === undefined || point.y === null) return
            if (!curve3ByX.has(point.x)) return
            // Two points sharing the same x draw a vertical segment, the NaN breaks the line before the next pair
            connector.push({x: point.x, y: point.y})
            connector.push({x: point.x, y: curve3ByX.get(point.x)})
            connector.push({x: point.x, y: NaN})
        })
        return connector
    }

    buildReminderAnnotations() {
        const annotations = {}
        const reminders = this.props.alertPointsReminder || []

        reminders.forEach((reminder, index) => {
            const eventTime = new Date(reminder.x)
            if (Number.isNaN(eventTime.getTime())) return

            annotations["reminderPoint_" + index] = {
                type: 'point',
                backgroundColor: 'transparent',
                borderColor: 'red',
                borderWidth: 1,
                pointStyle: 'crossRot',
                radius: 8,
                yAdjust: -6,
                xValue: eventTime.toISOString(),
                yValue: this.props.min1,
                yScaleID: 'y'
            }
            annotations["reminderLabel_" + index] = {
                type: 'label',
                color: 'red',
                position: {
                    x: 'center',
                    y: 'end'
                },
                content: () => '?',
                font: {
                    size: 10
                },
                yAdjust: -3,
                xValue: eventTime.toISOString(),
                yValue: this.props.min1,
                yScaleID: 'y'
            }
        })
        return annotations
    }

    updateXAxisDisplay(chart) {
        if (!chart || !chart.scales || !chart.scales.x) return
        const display = getXAxisDisplayConfig(chart.scales.x.min, chart.scales.x.max)
        chart.options.scales.x.time.unit = display.unit
        chart.options.scales.x.time.stepSize = display.stepSize
        chart.options.scales.x.ticks.maxTicksLimit = display.maxTicks
    }

    handleDoubleClick() {
        if (!this.myChart || !this.myChart.resetZoom) return
        this.myChart.resetZoom('none')
        this.updateXAxisDisplay(this.myChart)
        this.myChart.update('none')
    }

    componentDidMount() {
        // The gesture hint is only shown for the first 6 seconds so it never
        // covers the measurement tooltips on hover.
        this.hintTimer = setTimeout(() => this.setState({ showHint: false }), 6000)

        const ctx = this.getCanvas()
        if (ctx === null) return

        const localMaxX = this.props.curve1 !== undefined ? this.props.max_x : 0
        const localMinX = this.props.curve1 !== undefined ? this.props.min_x : 0
        const xAxisDisplay = getXAxisDisplayConfig(localMinX, localMaxX)

        this.myChart = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: [
                    {
                        label: this.props.curve1_name,
                        data: this.props.curve1 || [],
                        showLine: false,
                        fill: false,
                        pointBackgroundColor: this.props.curve1_color,
                        pointBorderWidth: 0,
                        pointRadius: this.props.pointRadius || 4,
                        yAxisID: 'y'
                    },
                    {
                        label: this.props.threshold1_name,
                        data: this.props.threshold1 || [],
                        borderColor: this.props.threshold1_color,
                        borderWidth: 1,
                        pointBackgroundColor: this.props.threshold1_color,
                        showLine: true,
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        yAxisID: 'y'
                    },
                    {
                        label: this.props.curve2_name,
                        data: this.props.curve2 || [],
                        showLine: false,
                        fill: false,
                        pointBackgroundColor: this.props.curve2_color,
                        pointBorderWidth: 0,
                        pointRadius: this.props.pointRadius || 4,
                        yAxisID: 'y1'
                    },
                    {
                        label: this.props.threshold2_name,
                        data: this.props.threshold2 || [],
                        borderColor: this.props.threshold2_color,
                        borderWidth: 1,
                        pointBackgroundColor: this.props.threshold2_color,
                        showLine: true,
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        yAxisID: 'y1'
                    },
                    {
                        label: this.props.curve3_name,
                        data: this.props.curve3 || [],
                        showLine: false,
                        fill: false,
                        pointBackgroundColor: this.props.curve3_color,
                        pointBorderWidth: 0,
                        pointRadius: this.props.pointRadius || 4,
                        yAxisID: 'y1'
                    },
                    {
                        label: this.props.threshold3_name,
                        data: this.props.threshold3 || [],
                        borderColor: this.props.threshold3_color,
                        borderWidth: 1,
                        pointBackgroundColor: this.props.threshold3_color,
                        showLine: true,
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        yAxisID: 'y1'
                    },
                    {
                        label: 'Maintenant',
                        data: this.props.currentTime || [],
                        borderColor: "#E7DFD8",
                        pointBackgroundColor: "#E7DFD8",
                        showLine: true,
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        yAxisID: 'y1'
                    },
                    {
                        label: 'tensionConnector',
                        data: this.buildConnectorData(),
                        borderColor: 'black',
                        borderWidth: 1,
                        showLine: true,
                        spanGaps: false,
                        fill: false,
                        pointRadius: 0,
                        pointHitRadius: 0,
                        yAxisID: 'y1'
                    }
                ],
            },
            options: {
                parsing: false,
                normalized: true,
                responsive: true,
                animation: false,
                hitRadius: 0,
                plugins: {
                    title: {
                        color: 'black',
                        font : {weight: 'bold'},
                        display: this.props.title !== undefined,
                        text: this.props.title
                    },
                    zoom: {
                        pan: {
                            enabled: true,
                            mode: 'x',
                            threshold: 5,
                            modifierKey: 'shift',
                            onPanComplete: ({chart}) => {
                                this.updateXAxisDisplay(chart)
                                chart.update('none')
                            }
                        },
                        limits: {
                            x: {min: 'original', max: 'original', minRange: 3600000},
                            y: {min: 'original', max: 'original'},
                        },
                        zoom: {
                            wheel: {
                                enabled: true,
                                speed: 0.1,
                            },
                            pinch: {
                                enabled: true
                            },
                            drag: {
                                enabled: true,
                                backgroundColor: 'rgba(40, 150, 229, 0.15)',
                                borderColor: 'rgba(40, 150, 229, 0.8)',
                                borderWidth: 1,
                                threshold: 5,
                            },
                            mode: 'x',
                            onZoomComplete: ({chart}) => {
                                this.updateXAxisDisplay(chart)
                                chart.update('none')
                            }
                        }
                    },
                    annotation: {
                        annotations: this.buildReminderAnnotations()
                    },
                    tooltip: {
                        backgroundColor:'rgba(0,0,0,0.8)',
                        // A tensiometer reading records heart rate, systolic and
                        // diastolic at the same time, so hovering near a point shows
                        // all three values for that timestamp. Only the measured
                        // points (showLine: false) are kept; the threshold lines,
                        // "Maintenant" marker and connector are filtered out.
                        mode: 'x',
                        intersect: false,
                        filter: (item) => item.dataset.showLine === false,
                    },
                    legend: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        id:'xAxis1',
                        type: 'time',
                        min: localMinX,
                        max: localMaxX,
                        distribution: 'series',
                        time: {
                            displayFormats: {
                                hour: 'HH:mm',
                                day: 'DD/MM',
                                month: 'MM/YYYY',
                                year: 'YYYY',
                            },
                            unit: xAxisDisplay.unit,
                            stepSize: xAxisDisplay.stepSize,
                            tooltipFormat: 'DD/MM/YYYY - HH:mm'
                        },
                        ticks: {
                            minRotation: 0,
                            maxRotation: 0,
                            maxTicksLimit: xAxisDisplay.maxTicks,
                            major: {
                                enabled: true
                            },
                            font: function(context) {
                                if (context.tick && context.tick.major) {
                                    return {
                                        weight: 'bold',
                                    };
                                }
                            }
                        }
                    },
                    y: {
                        min: this.props.min1,
                        max: this.props.max1,
                        title: {
                            display: this.props.y_axis_title1 !== undefined,
                            padding: 0,
                            text: this.props.y_axis_title1,
                            color: this.props.threshold1_color
                        },
                        position: 'left',
                        ticks: {
                            color: this.props.threshold1_color,
                        },
                        grid: {
                            drawOnChartArea: false,
                        }
                    },
                    y1: {
                        min: this.props.min2,
                        max: this.props.max2,
                        title: {
                            display: this.props.y_axis_title2 !== undefined,
                            padding: 0,
                            text: this.props.y_axis_title2,
                            color: this.props.threshold2_color
                        },
                        position: 'right',
                        major: {
                            enabled: false
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        ticks: {
                            color: this.props.threshold2_color,
                        },
                    },
                }
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (!this.myChart || !this.myChart.data) return

        const rangeChanged = prevProps.min_x !== this.props.min_x || prevProps.max_x !== this.props.max_x
        const dataChanged =
            prevProps.curve1 !== this.props.curve1 ||
            prevProps.threshold1 !== this.props.threshold1 ||
            prevProps.curve2 !== this.props.curve2 ||
            prevProps.threshold2 !== this.props.threshold2 ||
            prevProps.curve3 !== this.props.curve3 ||
            prevProps.threshold3 !== this.props.threshold3 ||
            prevProps.currentTime !== this.props.currentTime ||
            prevProps.alertPointsReminder !== this.props.alertPointsReminder

        if (!rangeChanged && !dataChanged) return
        if (this.myChart.isZoomingOrPanning && this.myChart.isZoomingOrPanning()) return
        // Skip refresh while zoomed/panned so the periodic update does not snap
        // the view back to the full range. Resumes once the graph is reset.
        if (this.myChart.isZoomedOrPanned && this.myChart.isZoomedOrPanned()) return

        this.myChart.data.datasets[0].data = this.props.curve1 || []
        this.myChart.data.datasets[1].data = this.props.threshold1 || []
        this.myChart.data.datasets[2].data = this.props.curve2 || []
        this.myChart.data.datasets[3].data = this.props.threshold2 || []
        this.myChart.data.datasets[4].data = this.props.curve3 || []
        this.myChart.data.datasets[5].data = this.props.threshold3 || []
        this.myChart.data.datasets[6].data = this.props.currentTime || []
        this.myChart.data.datasets[7].data = this.buildConnectorData()
        this.myChart.options.plugins.annotation.annotations = this.buildReminderAnnotations()

        if (rangeChanged) {
            this.myChart.options.scales.x.min = this.props.min_x
            this.myChart.options.scales.x.max = this.props.max_x
            const display = getXAxisDisplayConfig(this.props.min_x, this.props.max_x)
            this.myChart.options.scales.x.time.unit = display.unit
            this.myChart.options.scales.x.time.stepSize = display.stepSize
            this.myChart.options.scales.x.ticks.maxTicksLimit = display.maxTicks
        }

        this.myChart.update('none')
    }

    componentWillUnmount() {
        clearTimeout(this.hintTimer)
        if (this.myChart && this.myChart.destroy) {
            this.myChart.destroy()
        }
    }

    render() {
        return (
            <div
                className={classes.graphContainer}
                style={{position: "relative", display: "flex", flexDirection: "column", height: "100%", width: "100%"}}
            >
                {this.state.showHint && (
                    <div style={{
                        position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)",
                        zIndex: 6, pointerEvents: "none",
                        background: "rgba(0,0,0,0.7)", color: "#fff",
                        fontSize: 11, lineHeight: 1.3, padding: "4px 8px", borderRadius: 4,
                        maxWidth: "92%", textAlign: "center"
                    }}>
                        Molette/pincement ou sélection pour zoomer, Maj + glisser pour déplacer, double-clic pour réinitialiser
                    </div>
                )}
                <div style={{flex: "1 1 auto", minHeight: 0, width: "100%", display: "flex", justifyContent: 'center', alignItems: 'center'}}>
                    <canvas
                        style={{display: "flex", height: "100%", width: "100%", justifyContent:'center', alignItems:'center', touchAction: 'none', cursor: 'crosshair', userSelect: 'none'}}
                        id={this.props.id_graph}
                        ref={this.chartRef}
                        onDoubleClick={this.handleDoubleClick}
                    />
                </div>
                <GraphZoomControls
                    getChart={() => this.myChart}
                    afterChange={(chart) => { this.updateXAxisDisplay(chart); chart.update('none') }}
                    onEnlarge={this.props.onEnlarge}
                />
            </div>
        )
    }
}

export default (GraphThreeCurves)
